import {
	Box,
	createListCollection,
	For,
	Input,
	InputGroup,
	Portal,
	Select,
	type SelectControlProps,
	type SelectIndicatorGroupProps,
	type SelectIndicatorProps,
	type SelectItemIndicatorProps,
	type SelectPositionerProps,
	Tag,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useEffect, useState } from 'react';
import { BsArrowDown, BsArrowUp } from 'react-icons/bs';
import { FiFilter } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { TbFilterX } from 'react-icons/tb';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftDatePicker, { type DateRange } from '@/components/Molecules/FaceliftDatePicker';
import Show from '@/components/Molecules/Show';
import type {
	CustomSelectContentProps,
	CustomSelectItemProps,
	CustomSelectTriggerProps,
	CustomSelectValueTextProps,
	DataTableColumnFilterOptionsProps,
	ISelectCommonProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import type { DateFilterModule } from '@/store/dateFilterStore';
import { useDateFilterStore } from '@/store/dateFilterStore';
import { MenuPrimitives as CustomMenu } from '@/types/MenuPrimitives.types';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.tz.setDefault('America/New_York');

const CustomSelect = {
	Control: (props: SelectControlProps & ISelectCommonProps) => (
		<Select.Control {...props} />
	),
	Trigger: (props: CustomSelectTriggerProps) => <Select.Trigger {...props} />,
	ValueText: (props: CustomSelectValueTextProps) => (
		<Select.ValueText {...props} />
	),
	IndicatorGroup: (props: SelectIndicatorGroupProps & ISelectCommonProps) => (
		<Select.IndicatorGroup {...props} />
	),
	Indicator: (props: SelectIndicatorProps & ISelectCommonProps) => (
		<Select.Indicator {...props} />
	),
	Positioner: (props: SelectPositionerProps & ISelectCommonProps) => (
		<Select.Positioner {...props} />
	),
	Content: (props: CustomSelectContentProps) => <Select.Content {...props} />,
	Item: (props: CustomSelectItemProps) => <Select.Item {...props} />,
	ItemIndicator: (props: SelectItemIndicatorProps & ISelectCommonProps) => (
		<Select.ItemIndicator {...props} />
	),
	Portal: (props: React.ComponentProps<typeof Portal> & ISelectCommonProps) => (
		<Portal {...props} />
	),
};

const sortOptions = [
	{
		label: 'A - Z',
		type: 'string-status',
		direction: 'asc',
		icon: <BsArrowUp />,
	},
	{
		label: 'Z - A',
		type: 'string-status',
		direction: 'desc',
		icon: <BsArrowDown />,
	},
	{
		label: 'A - Z',
		type: 'string',
		direction: 'asc',
		icon: <BsArrowUp />,
	},
	{
		label: 'Z - A',
		type: 'string',
		direction: 'desc',
		icon: <BsArrowDown />,
	},
	{
		label: 'Older to Newer',
		type: 'date',
		direction: 'asc',
		icon: <BsArrowUp />,
	},
	{
		label: 'Newer to Older',
		type: 'date',
		direction: 'desc',
		icon: <BsArrowDown />,
	},
	{
		label: 'Smaller to Bigger',
		type: 'number',
		direction: 'asc',
		icon: <BsArrowUp />,
	},
	{
		label: 'Bigger to Smaller',
		type: 'number',
		direction: 'desc',
		icon: <BsArrowDown />,
	},
];

const commonOptions: SelectItem[] = [
	{ label: 'Contains', value: 'contains' },
	{ label: 'Does not contain', value: 'not_contains' },
	{ label: 'Begins with', value: 'begins_with' },
	{ label: 'Does not begin with', value: 'not_begins_with' },
	{ label: 'Ends with', value: 'ends_with' },
	{ label: 'Does not end with', value: 'not_ends_with' },
];

const aging: SelectItem[] = [
	{ label: 'Equals', value: 'equals' },
	{ label: 'Does not equal', value: 'not_equals' },
	{ label: 'Greater than', value: 'greater' },
	{ label: 'Less than', value: 'less' },
	{ label: 'Greater than and equal to', value: 'greater_equals' },
	{ label: 'Less than and equal to', value: 'less_equals' },
];

const statusAging: SelectItem[] = [
	{ label: 'Equals', value: 'in' },
	{ label: 'Does not equal', value: 'not_in' },
];

const dateRanges: SelectItem[] = [
	{ label: 'On', value: 'on' },
	{ label: 'Today', value: 'today' },
	{ label: 'This Week', value: 'this_week' },
	{ label: 'Last Week', value: 'last_week' },
	{ label: 'This Month', value: 'this_month' },
	{ label: 'Last Month', value: 'last_month' },
	{ label: 'This Year', value: 'this_year' },
	{ label: 'Last Year', value: 'last_year' },
	{ label: 'Year to Date', value: 'year_to_date' },
	{ label: 'Yesterday', value: 'yesterday' },
	{ label: 'Tomorrow', value: 'tomorrow' },
];

/**
 * Helper function to calculate date range label for a given filter type
 * This is extracted to be reusable and avoid duplication
 */
const calculateDateRangeLabel = (filterType: string, customDateRange?: DateRange): string => {
	const now = dayjs().tz('America/New_York');
	const format = 'MM/DD/YYYY';
	let startDate = '';
	let endDate = '';

	switch (filterType) {
		case 'on':
			return customDateRange ? `${customDateRange.startDate} to ${customDateRange.endDate}` : '';
		case 'today':
			startDate = now.startOf('day').format(format);
			return startDate;
		case 'yesterday':
			startDate = now.subtract(1, 'day').startOf('day').format(format);
			return startDate;
		case 'tomorrow':
			startDate = now.add(1, 'day').startOf('day').format(format);
			return startDate;
		case 'this_week':
			startDate = now.startOf('week').format(format);
			endDate = now.endOf('week').format(format);
			return `${startDate} to ${endDate}`;
		case 'last_week':
			startDate = now.subtract(1, 'week').startOf('week').format(format);
			endDate = now.subtract(1, 'week').endOf('week').format(format);
			return `${startDate} to ${endDate}`;
		case 'this_month':
			startDate = now.startOf('month').format(format);
			endDate = now.endOf('month').format(format);
			return `${startDate} to ${endDate}`;
		case 'last_month':
			startDate = now.subtract(1, 'month').startOf('month').format(format);
			endDate = now.subtract(1, 'month').endOf('month').format(format);
			return `${startDate} to ${endDate}`;
		case 'this_year':
			startDate = now.startOf('year').format(format);
			endDate = now.endOf('year').format(format);
			return `${startDate} to ${endDate}`;
		case 'last_year':
			startDate = now.subtract(1, 'year').startOf('year').format(format);
			endDate = now.subtract(1, 'year').endOf('year').format(format);
			return `${startDate} to ${endDate}`;
		case 'year_to_date':
			startDate = now.startOf('year').format(format);
			endDate = now.format(format);
			return `${startDate} to ${endDate}`;
		default:
			return '';
	}
};

const DataTableColumnFilterOptions = ({
	column,
	sort,
	setSort,
	columnsFilters,
	setColumnFilters,
}: DataTableColumnFilterOptionsProps) => {
	const menuBg = useColorModeValue('white', 'gray.700');
	const menuBorder = useColorModeValue('gray.300', 'gray.500');
	const [showSubMenu, setShowSubMenu] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const selectDefaultValue =
		column.type === 'string'
			? commonOptions[0]
			: column.type === 'date'
				? column.customDateRanges?.[0] || dateRanges[0]
				: column.type === 'string-status'
					? statusAging[0]
					: aging[0];

	const [selectedOption, setSelectedOption] = useState<SelectItem[] | null>([
		selectDefaultValue,
	]);
	const [selectedStatusOption, setSelectedStatusOption] = useState<
		SelectItem[] | null
	>(null);
	const [inputValue, setInputValue] = useState<string>('');
	const [dateRange, setDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});
	const [finalFilterAsString, setFinalFilterAsString] = useState('');

	// Initialize local state from existing column filters (for default filters)
	useEffect(() => {
		const existingFilter = columnsFilters.find((f) => f.key === column.key);

		if (existingFilter && column.type === 'date') {
			// Find module config that uses this column
			const modules = useDateFilterStore.getState().modules;
			const moduleConfig = modules.find((m: DateFilterModule) => m.columnKey === column.key);

			if (moduleConfig) {
				const option = (column.customDateRanges || dateRanges).find(
					(opt) => opt.value === moduleConfig.defaultDateFilter
				);
				if (option) {
					setSelectedOption([option]);
					
					// Use the shared helper function to calculate date range label
					const dateLabel = calculateDateRangeLabel(moduleConfig.defaultDateFilter);
					if (dateLabel) {
						setFinalFilterAsString(`${option.label} - ${dateLabel}`);
					} else {
						setFinalFilterAsString(option.label);
					}
				}
			}
		}
	}, []);

	const clearFilters = () => {
		setShowSubMenu(false);
		setSort(null);
		setIsOpen(false);
		setSelectedOption(null);
		setSelectedStatusOption(null);
		setInputValue('');
		const filtercolumnFilters = columnsFilters.filter(
			(colF) => colF.key !== column.key,
		);
		setColumnFilters(filtercolumnFilters);
		setDateRange({
			startDate: '',
			endDate: '',
		});
		setFinalFilterAsString('');
	};

	const closeFilterBy = () => {
		setShowSubMenu(false);
		setSelectedOption(null);
		setInputValue('');
	};

	const getDate = (date: DateRange, type: 'date' | 'dateISO' = 'date') => {
		if (!selectedOption?.[0]?.value) return '';
		const selectedValue = selectedOption[0].value;
		const now = dayjs().tz('America/New_York');
		let startDate = '';
		let endDate = '';
		
		// For ISO dates, use ISO format with timezone
		const format = type === 'dateISO' ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DD';
		
		switch (selectedValue) {
			case 'on': {
				if (type === 'dateISO') {
					// Parse start date parts and construct UTC date explicitly
					const [startMonth, startDay, startYear] = date.startDate?.split('/').map(Number) || [0, 0, 0];
					// Parse end date parts and construct UTC date explicitly
					const [endMonth, endDay, endYear] = date.endDate?.split('/').map(Number) || [0, 0, 0];
					// Create start date at midnight UTC
					const utcStartDate = dayjs.utc().year(startYear).month(startMonth - 1).date(startDay).hour(0).minute(0).second(0).millisecond(0);
					// Create end date at 23:59:59 UTC
					const utcEndDate = dayjs.utc().year(endYear).month(endMonth - 1).date(endDay).hour(23).minute(59).second(59).millisecond(0);
					startDate = utcStartDate.format(format);
					endDate = utcEndDate.format(format);
				} else {
					const parsedStartDate = dayjs(date.startDate, 'MM/DD/YYYY');
					const formattedStartDate = parsedStartDate.format('YYYY-MM-DD');
					const parsedEndDate = dayjs(date.endDate, 'MM/DD/YYYY');
					const formattedEndDate = parsedEndDate.format('YYYY-MM-DD');
					return `${formattedStartDate} || ${formattedEndDate}`;
				}
				return `${startDate} || ${endDate}`;
			}
			case 'today':
				if (type === 'dateISO') {
					startDate = now.utc().startOf('day').format(format);
					endDate = now.utc().endOf('day').format(format);
				} else {
					startDate = now.startOf('day').format('YYYY-MM-DD');
					endDate = now.endOf('day').format('YYYY-MM-DD');
				}
				break;
			case 'yesterday':
				if (type === 'dateISO') {
					startDate = now.subtract(1, 'day').utc().startOf('day').format(format);
					endDate = now.subtract(1, 'day').utc().endOf('day').format(format);
				} else {
					startDate = now.subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
					endDate = now.subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
				}
				break;
			case 'tomorrow':
				if (type === 'dateISO') {
					startDate = now.add(1, 'day').utc().startOf('day').format(format);
					endDate = now.add(1, 'day').utc().endOf('day').format(format);
				} else {
					startDate = now.add(1, 'day').startOf('day').format('YYYY-MM-DD');
					endDate = now.add(1, 'day').endOf('day').format('YYYY-MM-DD');
				}
				break;
			case 'this_week':
				if (type === 'dateISO') {
					startDate = now.utc().startOf('week').format(format);
					endDate = now.utc().endOf('week').format(format);
				} else {
					startDate = now.startOf('week').format('YYYY-MM-DD');
					endDate = now.endOf('week').format('YYYY-MM-DD');
				}
				break;
			case 'last_week':
				if (type === 'dateISO') {
					startDate = now.subtract(1, 'week').utc().startOf('week').format(format);
					endDate = now.subtract(1, 'week').utc().endOf('week').format(format);
				} else {
					startDate = now.subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
					endDate = now.subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
				}
				break;
			case 'this_month':
				if (type === 'dateISO') {
					startDate = now.utc().startOf('month').format(format);
					endDate = now.utc().endOf('month').format(format);
				} else {
					startDate = now.startOf('month').format('YYYY-MM-DD');
					endDate = now.endOf('month').format('YYYY-MM-DD');
				}
				break;
			case 'last_month':
				if (type === 'dateISO') {
					startDate = now.subtract(1, 'month').utc().startOf('month').format(format);
					endDate = now.subtract(1, 'month').utc().endOf('month').format(format);
				} else {
					startDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
					endDate = now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
				}
				break;
			case 'this_year':
				if (type === 'dateISO') {
					startDate = now.utc().startOf('year').format(format);
					endDate = now.utc().endOf('year').format(format);
				} else {
					startDate = now.startOf('year').format('YYYY-MM-DD');
					endDate = now.endOf('year').format('YYYY-MM-DD');
				}
				break;
			case 'last_year':
				if (type === 'dateISO') {
					startDate = now.subtract(1, 'year').utc().startOf('year').format(format);
					endDate = now.subtract(1, 'year').utc().endOf('year').format(format);
				} else {
					startDate = now.subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
					endDate = now.subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
				}
				break;
			case 'year_to_date':
				if (type === 'dateISO') {
					startDate = now.utc().startOf('year').format(format);
					endDate = now.utc().format(format);
				} else {
					startDate = now.startOf('year').format('YYYY-MM-DD');
					endDate = now.format('YYYY-MM-DD');
				}
				break;
			default:
				return '';
		}

		return `${startDate} || ${endDate}`;
	};

	const getDateLabel = (date: DateRange) => {
		if (!selectedOption?.[0]?.value) return '';
		return calculateDateRangeLabel(String(selectedOption[0].value), date);
	};

	const onApply = () => {
		setIsOpen(false);
		setShowSubMenu(false);
		const filtercolumnFilters = columnsFilters.filter(
			(colF) => colF.key !== column.key,
		);
		setColumnFilters([
			...filtercolumnFilters,
			{
				key: column.key,
				type: column.type || '',
				value:
					column.type === 'date'
						? getDate(dateRange, column.dateISO ? 'dateISO' : 'date')
						: column.type === 'string-status'
							? (selectedStatusOption || []).map((opt) => opt.value)
							: inputValue,
				operator:
					column.type === 'string-status'
						? (selectedOption?.[0]?.value?.toString() || 'in')
						: (column.type !== 'date' && selectedOption?.[0]?.value?.toString()) ||
							'equals',
			},
		]);
		setFinalFilterAsString(
			column.type === 'string-status'
				? selectedStatusOption?.length === 1
					? `${selectedOption?.[0]?.label} - ${selectedStatusOption?.[0]?.label}`
					: `${selectedStatusOption?.length} item selected`
				: `${selectedOption?.[0]?.label} - ${column.type === 'date' ? getDateLabel(dateRange) : inputValue}`,
		);
	};

	const frameworks = createListCollection({
		items:
			column.type === 'string'
				? commonOptions
				: column.type === 'date'
					? column.customDateRanges || dateRanges
					: column.type === 'string-status'
						? statusAging
						: aging,
	});

	const statusFrameworks = createListCollection({
		items: column.filterOptions || [],
	});

	const removeFilter = (val: string | number) => {
		setSelectedStatusOption((prev) =>
			(prev || []).filter((fil) => fil.value !== val),
		);
	};

	const disabledApply = () => {
		if (column.type === 'date') {
			return selectedOption?.[0]?.value === 'on'
				? !dateRange.startDate || !dateRange.endDate
				: !selectedOption;
		}
		if (column.type === 'string' || column.type === 'number') {
			return !selectedOption || !inputValue;
		}
		if (column.type === 'string-status') {
			return !selectedOption || !selectedStatusOption || selectedStatusOption?.length === 0;
		}
		return true;
	};
	const handleChangeInputValue = (e: { target: { value: string } }) => {
		const input = e.target.value;
		if (column.type === 'number') {
			const numericValue = input.replace(/[^0-9.]/g, '');
			const parts = numericValue?.split('.');
			const finalValue = parts.length > 2
				? `${parts[0]}.${parts[1]}`
				: numericValue;

			setInputValue(finalValue);
			return;
		}
		setInputValue(e.target.value)
	}
	return (
		<CustomMenu.Root
			open={isOpen}
			onOpenChange={(e: { open: boolean }) => {
				if (showSubMenu && !e.open) {
					return;
				}
				setIsOpen(e.open);
			}}
			closeOnSelect={false}
		>
			<CustomMenu.Trigger width="100%" outline="none">
				<InputGroup
					endElement={
						sort?.key === column.key ? (
							sort?.direction === 'asc' ? (
								<Box color={{ base: "gray.700", _dark: "white" }}>
									<BsArrowUp />
								</Box>
							) : (
								<Box color={{ base: "gray.700", _dark: "white" }}>
									<BsArrowDown />
								</Box>
							)
						) : (
							''
						)
					}
				>
					<Input
						px={2}
						height={6}
						outline={0}
						fontSize="xs"
						fontWeight={400}
						bg={{ base: "white", _dark: "transparent" }}
						color={{ base: "gray.700", _dark: "white" }}
						borderRadius={20}
						border="1px solid"
						placeholder="Filter"
						readOnly={true}
						defaultValue={finalFilterAsString}
						borderColor="brand.inputText"
						_placeholder={{ color: { base: "gray.700", _dark: "white" } }}
					/>
				</InputGroup>
			</CustomMenu.Trigger>
			<Portal>
				<CustomMenu.Positioner>
					<CustomMenu.Content
						width={180}
						zIndex={1500}
						borderRadius={8}
						bg={menuBg}
						overflow="visible"
						boxShadow={`0px 4px 4px 0px ${colors.menuListShadow}`}
					>
						<Show when={showSubMenu}>
							<Box p={2}>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									fontSize="xs"
									mb={3}
								>
									<FaceliftText>Filter By</FaceliftText>
									<FaceliftText onClick={closeFilterBy} cursor="pointer">
										<MdClose size={16} />
									</FaceliftText>
								</Box>
								<Box display="flex" flexDirection="column" gap={2}>
									<Select.Root
										size="xs"
										collection={frameworks}
										value={
											selectedOption
												? selectedOption.map((item) => item.value)
												: []
										}
										onValueChange={(e: { items: SelectItem[] }) => {


											setInputValue('')
											setSelectedStatusOption(null)
											setDateRange({
												startDate: '',
												endDate: '',
											});
											setSelectedOption(e.items);
										}}
									>
										<Select.HiddenSelect />
										<CustomSelect.Control>
											<CustomSelect.Trigger
												border="1px solid"
												borderColor={menuBorder}
											>
												<CustomSelect.ValueText placeholder="Select" />
											</CustomSelect.Trigger>
											<CustomSelect.IndicatorGroup>
												<CustomSelect.Indicator />
											</CustomSelect.IndicatorGroup>
										</CustomSelect.Control>

										<CustomSelect.Portal>
											<CustomSelect.Positioner>
												<CustomSelect.Content zIndex={1600}>
													{frameworks.items.map((framework: SelectItem) => (
														<CustomSelect.Item
															item={framework}
															key={framework.value}
														>
															{framework.label}
															<Select.ItemIndicator />
														</CustomSelect.Item>
													))}
												</CustomSelect.Content>
											</CustomSelect.Positioner>
										</CustomSelect.Portal>
									</Select.Root>
									<Show
										when={
											column.type !== 'date' && column.type !== 'string-status'
										}
									>
										<Input
											px={2}
											height={8}
											borderColor={menuBorder}
											fontSize="xs"
											value={inputValue}
											onChange={handleChangeInputValue}
										/>
									</Show>
									<Show when={column.type === 'string-status'}>
										<Select.Root
											multiple
											size="xs"
											collection={statusFrameworks}
											value={
												selectedStatusOption
													? selectedStatusOption.map((item) => item.value)
													: []
											}
											onValueChange={(e: { items: SelectItem[] }) => {
												setSelectedStatusOption(e.items);
											}}
										>
											<Select.HiddenSelect />
											<CustomSelect.Control>
												<CustomSelect.Trigger
													border="1px solid"
													borderColor={menuBorder}
												>
													<Box
														display="flex"
														flexWrap="wrap"
														gap={2}
														alignItems="center"
														p={1}
														mr={3}
													>
														{selectedStatusOption &&
															selectedStatusOption.length > 0 ? (
															selectedStatusOption.map((item) => (
																<Tag.Root key={item.value}>
																	<Tag.Label>{item.label}</Tag.Label>
																	<Tag.EndElement>
																		<Tag.CloseTrigger
																			onClick={() => removeFilter(item.value)}
																		/>
																	</Tag.EndElement>
																</Tag.Root>
															))
														) : (
															<Box color="gray.500">Select</Box>
														)}
													</Box>
												</CustomSelect.Trigger>
												<CustomSelect.IndicatorGroup>
													<CustomSelect.Indicator />
												</CustomSelect.IndicatorGroup>
											</CustomSelect.Control>

											<CustomSelect.Portal>
												<CustomSelect.Positioner>
													<CustomSelect.Content zIndex={1600}>
														{statusFrameworks.items.map(
															(framework: SelectItem) => (
																<CustomSelect.Item
																	item={framework}
																	key={framework.value}
																>
																	{framework.label}
																	<Select.ItemIndicator />
																</CustomSelect.Item>
															),
														)}
													</CustomSelect.Content>
												</CustomSelect.Positioner>
											</CustomSelect.Portal>
										</Select.Root>
									</Show>
									<Show
										when={
											column.type === 'date' &&
											selectedOption?.[0]?.value === 'on'
										}
									>
										<Box zIndex={1700}>
											<FaceliftDatePicker
												isRange={true}
												topLabel="Select Date Range"
												range={dateRange}
												onRangeChange={setDateRange}
											/>
										</Box>
									</Show>
									<Box textAlign="right">
										<FaceliftButton
											height={6}
											width={100}
											fontSize="xs"
											bg="brand.solid"
											disabled={disabledApply()}
											onClick={onApply}
										>
											Apply
										</FaceliftButton>
									</Box>
								</Box>
							</Box>
						</Show>
						<Show when={!showSubMenu}>
							<For each={sortOptions}>
								{({ label, icon, type, direction }) => (
									<Show when={column.type === type && !!column.sorter} key={label + type}>
										<CustomMenu.Item
											key={label}
											px={3}
											py={1}
											cursor="pointer"
											borderRadius={4}
											onClick={() => {
												if (
													sort?.direction === direction &&
													sort?.key === column.key
												) {
													return;
												}
												setSort({ key: column.key, direction });
											}}
											_hover={{
												bg: 'brand.solidGradient',
												color: 'white',
											}}
										>
											{icon} {label}
										</CustomMenu.Item>
									</Show>
								)}
							</For>
						</Show>
						<Show when={!showSubMenu}>
							<Show when={!column.hideFilterBy}>
								<CustomMenu.Item
									key="filter-by"
									px={3}
									py={1}
									cursor="pointer"
									borderRadius={4}
									onClick={() => setShowSubMenu(true)}
									_hover={{
										bg: 'brand.solidGradient',
										color: 'white',
									}}
								>
									<FiFilter /> Filter by
								</CustomMenu.Item>
							</Show>
							<CustomMenu.Item
								key="clear-filter-by"
								px={3}
								py={1}
								cursor="pointer"
								borderRadius={4}
								onClick={clearFilters}
								_hover={{
									bg: 'brand.solidGradient',
									color: 'white',
								}}
							>
								<TbFilterX /> Clear filter
							</CustomMenu.Item>
						</Show>
					</CustomMenu.Content>
				</CustomMenu.Positioner>
			</Portal>
		</CustomMenu.Root>
	);
};

export default DataTableColumnFilterOptions;
